import { MoveHorizontal } from "lucide-react";
import { traceNodes } from "../data/seed";
import { useDepth } from "../presentation";
import { useScenario } from "../simulation/ScenarioContext";
import { StatusPill } from "./StatusPill";

type ImpactTraceProps = {
  compact?: boolean;
};

function statusTone(status: string) {
  if (status === "healthy") return "healthy" as const;
  if (status === "degraded") return "warning" as const;
  return "critical" as const;
}

export function ImpactTrace({ compact = false }: ImpactTraceProps) {
  const { depth } = useDepth();
  const { selectedTraceNodeId, selectTraceNode, stage, selectedDecision } = useScenario();
  const recovered = stage === "recovered";
  const visibleNodes = depth === "overview"
    ? traceNodes.filter((node) => ["INC-2048", "svc-staff-wifi", "TEL-0318", "USR-PRIYA-NAIR"].includes(node.id))
    : depth === "operations"
      ? traceNodes.filter((node) => !["NA-MDF-PP03:18", "NA-214-A1"].includes(node.id))
      : traceNodes;

  return (
    <div className={compact ? "impact-trace impact-trace--compact" : "impact-trace"} data-trace-depth={depth}>
      <div className="impact-trace__legend">
        <span><i className="trace-key trace-key--impact" /> Incident path</span>
        <span><i className="trace-key trace-key--verified" /> Verified relationship</span>
        <span>
          {depth === "overview"
            ? "4 outcome records"
            : depth === "operations"
              ? "7 working records"
              : "9 typed records and physical terminations"}
        </span>
      </div>
      <div className="trace-scroll-cue">
        <MoveHorizontal aria-hidden="true" size={16} />
        Scroll the relationship path
      </div>
      <div className="trace-path" role="list" aria-label="Incident impact relationship path">
        {visibleNodes.map((node, index) => {
          const status = recovered ? "healthy" : node.status;
          const detail =
            depth === "overview"
              ? node.detail
              : depth === "architecture"
                ? node.architectureDetail
                : node.operationalDetail;
          return (
            <div className="trace-path__item" key={node.id} role="listitem">
              {index > 0 && <span className="trace-connector" aria-hidden="true">→</span>}
              <button
                aria-label={`Inspect ${node.eyebrow} ${node.label}`}
                aria-pressed={selectedTraceNodeId === node.id}
                className={selectedTraceNodeId === node.id ? "trace-node is-selected" : "trace-node"}
                onClick={() => selectTraceNode(node.id)}
                type="button"
              >
                <span className="trace-node__topline">
                  <span>{node.eyebrow}</span>
                  <StatusPill label={status} tone={statusTone(status)} />
                </span>
                <strong>{node.label}</strong>
                {!compact && <small>{detail}</small>}
                {selectedDecision && node.id === "INC-2048" && (
                  <em>{selectedDecision.recoveryPercent}% recovery branch</em>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
